import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import TextInputFieldWithValidation from '../../ValidationFormElements/TextInputFieldWithValidation';
import handleChange from '../../../helpers/formInputHelpers';

import commonStyles from '../formGroupsCommon.module.scss';

const EmailPasswordForm = ({ formProps, showPasswordFields, initialData }) => {
  const { t } = useTranslation();
  const { register, errors, setValue, triggerValidation } = formProps;

  const [form, setForm] = useState({
    email: '',
    emailConfirm: '',
    password: '',
    repeatPassword: '',
  });

  useEffect(() => {
    setForm(() => ({
      email: initialData.email,
      emailConfirm: initialData.emailConfirm ?? initialData.email,
      password: initialData.password,
      repeatPassword: initialData.repeatPassword,
    }));
  }, [initialData]);

  useEffect(() => {
    register({ name: 'email' });
    register({ name: 'emailConfirm' });
    register({ name: 'password' });
    register({ name: 'repeatPassword' });
  }, [register]);

  useEffect(() => {
    const { email } = initialData;
    setValue('email', email, !!email);
    setValue('emailConfirm', email, !!email);
  }, [initialData, setValue]);

  const handleChangeInput = useCallback(
    (key, value) => handleChange(key, value, setForm, setValue),
    [setValue],
  );

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <TextInputFieldWithValidation
          required
          formProps={formProps}
          labelKey="passwordEmail.email"
          name="email"
          autoComplete="off"
          handleChange={(e) => {
            handleChangeInput('email', e.target.value);
            if (errors.emailConfirm) {
              triggerValidation({ name: 'emailConfirm' });
            }
          }}
          value={form.email}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInputFieldWithValidation
          required
          formProps={formProps}
          labelKey="passwordEmail.emailConfirm"
          name="emailConfirm"
          autoComplete="off"
          onPaste={(e) => e.preventDefault()}
          handleChange={(e) => handleChangeInput('emailConfirm', e.target.value)}
          value={form.emailConfirm}
        />
      </Grid>
      {showPasswordFields && (
        <>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="passwordEmail.password"
              name="password"
              autoComplete="off"
              handleChange={(e) => {
                handleChangeInput('password', e.target.value);
                if (errors.repeatPassword) {
                  triggerValidation({ name: 'repeatPassword' });
                }
              }}
              value={form.password}
              type="password"
            />
            <div className={commonStyles.hint}>{t('changePassword.passwordHint')}</div>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="passwordEmail.repeatPassword"
              name="repeatPassword"
              autoComplete="off"
              handleChange={(e) => handleChangeInput('repeatPassword', e.target.value)}
              value={form.repeatPassword}
              type="password"
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

EmailPasswordForm.propTypes = {
  initialData: PropTypes.shape({
    email: PropTypes.string,
    emailConfirm: PropTypes.string,
    password: PropTypes.string,
    repeatPassword: PropTypes.string,
  }),
  formProps: PropTypes.shape({
    errors: PropTypes.shape({
      emailConfirm: PropTypes.shape({}),
      repeatPassword: PropTypes.shape({}),
    }).isRequired,
    clearError: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    triggerValidation: PropTypes.func.isRequired,
    getValues: PropTypes.func,
    register: PropTypes.func.isRequired,
  }).isRequired,
  showPasswordFields: PropTypes.bool,
};

EmailPasswordForm.defaultProps = {
  initialData: {},
  showPasswordFields: true,
};

export default EmailPasswordForm;
